import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { Segmented, type SegmentedOption } from '@/components/synapse/Segmented';
import { DatePicker } from '@/components/synapse/DatePicker';
import type { PatientSex } from '@/types/patients';
import {
  todayIsoDate,
  type PatientDetailsDraft,
  type PatientDetailsErrors,
} from './patientDetails';

const SEX_OPTIONS: SegmentedOption<PatientSex>[] = [
  { value: 'male', labelKey: 'patients.sexMale' },
  { value: 'female', labelKey: 'patients.sexFemale' },
  { value: 'other', labelKey: 'patients.sexOther' },
];

interface Props {
  value: PatientDetailsDraft;
  onChange: (next: PatientDetailsDraft) => void;
  /**
   * Per-field validation messages. `fullName` is a ready-to-render string (each
   * surface words it differently); `phone` and `nationalId` are i18n keys from
   * `validatePatientDetails`, translated here so every surface shows the same
   * wording for the same rule.
   */
  errors?: PatientDetailsErrors | null;
  autoFocusName?: boolean;
  disabled?: boolean;
}

/**
 * The registration field set — name, sex, DOB/age, phone, NIC — with no submit
 * button, no photo, and no knowledge of where it is being submitted to.
 *
 * Extracted so the three places a patient's details are entered stay identical:
 * staff quick-create, public self-registration, and the staff review of a
 * submission. Previously this lived inline in QuickCreatePatient; a second copy
 * for the public form is exactly how the two would have drifted (a longer name
 * limit here, a missing hint there) without anyone noticing.
 *
 * The i18n keys stay under `patients.quickCreate.*` — they are the same labels
 * for the same fields, and renaming them would churn three locale files to no
 * benefit.
 */
export function PatientDetailsFields({
  value,
  onChange,
  errors,
  autoFocusName,
  disabled,
}: Props) {
  const { t } = useTranslation();

  // A key from validatePatientDetails becomes a message; a caller-supplied
  // string (fullName) passes through untouched.
  const message = (key: string | null | undefined) => (key ? t(key) : null);

  const set = <K extends keyof PatientDetailsDraft>(
    key: K,
    next: PatientDetailsDraft[K],
  ) => onChange({ ...value, [key]: next });

  // The approximate-DOB hint only makes sense while an age is standing in for a
  // real date of birth.
  const usingApproxDob = value.dob == null && value.age.trim() !== '';

  return (
    <>
      <Field
        label={t('patients.quickCreate.fullName')}
        hint={t('patients.quickCreate.fullNameHint')}
        error={errors?.fullName ?? null}
      >
        {(id) => (
          <div className="relative flex items-center">
            <Input
              id={id}
              value={value.fullName}
              onChange={(e) => set('fullName', e.target.value)}
              autoFocus={autoFocusName}
              disabled={disabled}
              className="pr-9"
            />
            <Icon
              name="user"
              size={15}
              className="pointer-events-none absolute right-3 text-muted-2"
            />
          </div>
        )}
      </Field>

      <Field label={t('patients.quickCreate.sex')} hint={t('patients.quickCreate.sexHint')}>
        {() => (
          <Segmented
            value={value.sex}
            onChange={(next) => set('sex', next)}
            options={SEX_OPTIONS}
            ariaLabel={t('patients.quickCreate.sex')}
          />
        )}
      </Field>

      <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
        <Field label={t('patients.quickCreate.dateOfBirth')}>
          {(id) => (
            <DatePicker
              id={id}
              value={value.dob}
              onChange={(next) => set('dob', next)}
              clearable
              max={todayIsoDate()}
            />
          )}
        </Field>
        <Field
          label={t('patients.quickCreate.age')}
          optional
          hint={
            usingApproxDob
              ? t('patients.quickCreate.ageApproxHint')
              : t('patients.quickCreate.ageHint')
          }
        >
          {(id) => (
            <div className="relative flex items-center">
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={0}
                max={150}
                value={value.age}
                onChange={(e) => set('age', e.target.value)}
                // A real DOB makes the age redundant, and letting both be set
                // invites a contradiction we'd have to arbitrate.
                disabled={disabled || value.dob != null}
                className="pr-12"
              />
              <span className="pointer-events-none absolute right-3 text-[12px] text-muted-2">
                {t('patients.quickCreate.ageUnit')}
              </span>
            </div>
          )}
        </Field>
      </div>

      <Field
        label={t('patients.quickCreate.phone')}
        error={message(errors?.phone)}
      >
        {(id) => (
          <Input
            id={id}
            value={value.phone}
            onChange={(e) => set('phone', e.target.value)}
            disabled={disabled}
            mono
            inputMode="tel"
          />
        )}
      </Field>

      <Field
        label={t('patients.quickCreate.nationalId')}
        optional
        hint={t('patients.quickCreate.nationalIdHint')}
        error={message(errors?.nationalId)}
      >
        {(id) => (
          <Input
            id={id}
            value={value.nationalId}
            onChange={(e) => set('nationalId', e.target.value)}
            placeholder={t('patients.quickCreate.nationalIdPlaceholder')}
            disabled={disabled}
            mono
          />
        )}
      </Field>
    </>
  );
}
